import type { Category, Plan } from "./schema.js";

export const STATION_IDS = [
  "skierg",
  "sled-push",
  "sled-pull",
  "burpee-broad-jumps",
  "row",
  "farmers-carry",
  "sandbag-lunges",
  "wall-balls",
] as const;

export type StationId = (typeof STATION_IDS)[number];

export interface StationDefinition {
  id: StationId;
  name: string;
  /** Short label for small screens (watch faces); at most 8 characters. */
  shortName: string;
  amount: number;
  unit: "m" | "reps";
}

/** The eight stations in race order. */
export const STATIONS: readonly StationDefinition[] = [
  { id: "skierg", name: "SkiErg", shortName: "SkiErg", amount: 1000, unit: "m" },
  { id: "sled-push", name: "Sled Push", shortName: "Push", amount: 50, unit: "m" },
  { id: "sled-pull", name: "Sled Pull", shortName: "Pull", amount: 50, unit: "m" },
  { id: "burpee-broad-jumps", name: "Burpee Broad Jumps", shortName: "BBJ", amount: 80, unit: "m" },
  { id: "row", name: "Row", shortName: "Row", amount: 1000, unit: "m" },
  { id: "farmers-carry", name: "Farmers Carry", shortName: "Farmers", amount: 200, unit: "m" },
  { id: "sandbag-lunges", name: "Sandbag Lunges", shortName: "Lunges", amount: 100, unit: "m" },
  { id: "wall-balls", name: "Wall Balls", shortName: "WallBall", amount: 100, unit: "reps" },
];

type Sex = "women" | "men";

/** Station load in kg; farmers carry is per hand. Stations without a load are omitted. */
const STATION_WEIGHTS_KG: Record<Category, Record<Sex, Partial<Record<StationId, number>>>> = {
  open: {
    women: {
      "sled-push": 102,
      "sled-pull": 78,
      "farmers-carry": 16,
      "sandbag-lunges": 10,
      "wall-balls": 4,
    },
    men: {
      "sled-push": 152,
      "sled-pull": 103,
      "farmers-carry": 24,
      "sandbag-lunges": 20,
      "wall-balls": 6,
    },
  },
  pro: {
    women: {
      "sled-push": 152,
      "sled-pull": 103,
      "farmers-carry": 24,
      "sandbag-lunges": 20,
      "wall-balls": 6,
    },
    men: {
      "sled-push": 202,
      "sled-pull": 153,
      "farmers-carry": 32,
      "sandbag-lunges": 30,
      "wall-balls": 9,
    },
  },
};

/**
 * Station load label for a plan's category and division, e.g. "152 kg" or "2 × 24 kg".
 * Mixed doubles uses the men's loads for everyone; mixed relay athletes use their own
 * sex's load, so it shows both, e.g. "102 / 152 kg". Null for stations without a load
 * (SkiErg, Row, Burpee Broad Jumps).
 */
export function stationWeightLabel(
  stationId: StationId,
  { format, category, division }: Pick<Plan, "format" | "category" | "division">,
): string | null {
  const sexes: Sex[] =
    division !== "mixed" ? [division] : format === "relay" ? ["women", "men"] : ["men"];
  const loads = sexes.map((sex) => STATION_WEIGHTS_KG[category][sex][stationId]);
  if (loads.some((kg) => kg === undefined)) return null;
  const pair = stationId === "farmers-carry" ? "2 × " : "";
  return `${pair}${loads.join(" / ")} kg`;
}

export const RACE_LEGS = STATIONS.length;
export const RUN_DISTANCE_KM = 1;
/** In a relay each athlete covers this many run + station legs, in any order. */
export const RELAY_LEGS_PER_ATHLETE = 2;
